// At the top of the file, outside the hook
let redirectAttemptProcessed = false;

import { useEffect, useState } from 'react';
import { onAuthStateChanged, getRedirectResult, firebaseAuth } from '../libs/firebase/auth';
import { useCSRF } from '../contexts/csrf-context'; // Import useCSRF

const isClientEnvironment = () => typeof window !== 'undefined';

export function useUserSession(initSession) {
  // E2E Test Mode: Check for test mode flag synchronously on initial render
  // This ensures test user ID is available immediately, not after useEffect
  const getInitialUserId = () => {
    if (!isClientEnvironment()) {
      return initSession;
    }
    
    // Check for E2E test mode synchronously
    const isTestMode = 
      typeof window !== 'undefined' && 
      (window.__E2E_TEST_MODE__ === true || localStorage.getItem('__E2E_TEST_MODE__') === 'true');
    
    if (isTestMode) {
      return window.__TEST_USER_ID__ || 
             localStorage.getItem('__TEST_USER_ID__') || 
             'test-user-id';
    }
    
    return initSession;
  };

  const [userUid, setUserUid] = useState(getInitialUserId);
  const { refreshCSRF } = useCSRF(); // Get the refresh function

  // E2E Test Mode: Check for test mode flag and return test user ID
  useEffect(() => {
    if (!isClientEnvironment()) {
      return undefined;
    }
    
    // Check for E2E test mode
    const isTestMode = 
      typeof window !== 'undefined' && 
      (window.__E2E_TEST_MODE__ === true || localStorage.getItem('__E2E_TEST_MODE__') === 'true');
    
    if (isTestMode) {
      const testUserId = 
        window.__TEST_USER_ID__ || 
        localStorage.getItem('__TEST_USER_ID__') || 
        'test-user-id';
      // Only update if different (avoid unnecessary re-renders)
      if (userUid !== testUserId) {
        setUserUid(testUserId);
      }
      return undefined; // Don't set up Firebase Auth in test mode
    }

    let unsubFromAuthStateChanged;

    const initializeAuth = async () => {
      if (!firebaseAuth) {
        return;
      }

      if (!redirectAttemptProcessed) {
        redirectAttemptProcessed = true; // Set flag immediately on first attempt
        try {
          const result = await getRedirectResult(firebaseAuth);

          if (result && result.user) {
            setUserUid(result.user.uid);
          } else {
            if (firebaseAuth.currentUser) {
              setUserUid(firebaseAuth.currentUser.uid);
            }
          }
        } catch (error) {
          console.error('useUserSession: Error processing redirect result', error);
        }
      } else {
        if (firebaseAuth.currentUser) {
          setUserUid(firebaseAuth.currentUser.uid);
        }
      }

      unsubFromAuthStateChanged = onAuthStateChanged(async (authUser) => {
        if (authUser) {
          setUserUid(authUser.uid);
        } else {
          setUserUid(null);
        }
      });
    };

    initializeAuth();

    return () => {
      if (unsubFromAuthStateChanged) {
        unsubFromAuthStateChanged();
      }
      // Important: Reset redirectAttemptProcessed if the app has a way to truly navigate away and back
      // in a way that should allow a new redirect to be processed. For a typical SPA, this might be okay,
      // but for hard reloads or distinct navigation events, this flag might need more nuanced handling
      // or be reset on major navigation events if redirects can happen multiple times in an app lifetime.
      // For now, this makes it once per JavaScript context load.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- NEW useEffect to handle session creation with the backend ---
  useEffect(() => {
    if (!isClientEnvironment()) {
      return;
    }

    const createBackendSession = async (firebaseUser) => {
      if (!firebaseUser) return;

      let timeoutId;
      try {
        const idToken = await firebaseUser.getIdToken();

        // Create abort controller for timeout
        const controller = new AbortController();
        timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Include cookies for session
          body: JSON.stringify({ token: idToken }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (response.ok) {
          await response.json();
          // After successful login, refresh the CSRF token
          await refreshCSRF();
        } else {
          // Only log if it's not a network error (which is expected in some cases)
          if (response.status !== 0) {
            const errorData = await response.text().catch(() => '');
            console.warn('useUserSession: Failed to establish server session. Status:', response.status, errorData ? `Response: ${errorData}` : '');
          }
        }
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId);
        // Suppress network errors that are likely from browser extensions or offline state
        if (error.name === 'AbortError' || (error.name === 'TypeError' && error.message.includes('Failed to fetch'))) {
          // Network errors are expected in some scenarios (extensions, offline, etc.)
          // Don't spam console with these - they're handled gracefully
          return;
        }
        console.error('useUserSession: Error during backend session creation:', error);
      }
    };

    if (userUid && firebaseAuth?.currentUser) {
      // Check if the userUid state is set AND firebaseAuth.currentUser is available
      // This ensures we have the actual Firebase user object to call getIdToken()
      createBackendSession(firebaseAuth.currentUser);
    }
  }, [userUid, refreshCSRF]); // This effect runs when userUid changes

  return userUid;
} 